/**
 * A topic <select> grouped by unit, so the flat topic list reads as the
 * textbook's chapter structure (Unit 1, Unit 2, ...). Options are grouped with
 * native <optgroup>, which renders a bold, non-selectable header with its
 * topics indented beneath - accessible and consistent across the Home,
 * Practice, and Quiz screens.
 *
 * Only generator-backed topics are listed (the rest are "coming soon" and not
 * yet practiceable), matching what each screen showed before.
 */

import { el } from "./dom";
import {
  UNITS,
  topicsInUnit,
  isTopicId,
  type TopicId,
} from "../curriculum/curriculum";
import { hasGenerator } from "../topics/registry";

export interface TopicSelectOptions {
  /** Currently selected topic. */
  readonly selected: TopicId;
  /** aria-label for the control. */
  readonly ariaLabel: string;
  /** Called with the chosen topic when the selection changes. */
  readonly onChange: (topic: TopicId) => void;
}

/** Build a unit-grouped topic dropdown. */
export function createTopicSelect(opts: TopicSelectOptions): HTMLSelectElement {
  const groups = UNITS.map((unit) => {
    const options = topicsInUnit(unit.id)
      .filter((t) => hasGenerator(t.id))
      .map((t) =>
        el(
          "option",
          { value: t.id, selected: t.id === opts.selected },
          t.name,
        ),
      );
    if (options.length === 0) return null;
    return el(
      "optgroup",
      { label: `Unit ${unit.order}: ${unit.name}` },
      ...options,
    );
  }).filter((g): g is HTMLOptGroupElement => g !== null);

  return el(
    "select",
    {
      class: "select",
      "aria-label": opts.ariaLabel,
      onChange: (e) => {
        const value = (e.currentTarget as HTMLSelectElement).value;
        if (isTopicId(value)) opts.onChange(value);
      },
    },
    ...groups,
  ) as HTMLSelectElement;
}
